import { supabase } from "../supabase/client";

export interface LevelProgress {
  stars?: number;
  latestStars?: number;
  attempts?: number;
  completedAt?: string;
  subject?: string;
  level?: number;
  title?: string;
}

export interface ChestProgress {
  claimed?: boolean;
  diamonds?: number;
  claimedAt?: string;
}

export interface WorldState {
  diamonds: number;
  levels: Record<string, LevelProgress>;
  chests: Record<string, ChestProgress>;
}

const LEGACY_STORAGE_KEY = "lusky:world-progress:v1";
const storageKey = (childId: string | null) => `lusky:world-progress:v2:${childId || "guest"}`;
const isCloudChild = (childId: string | null): childId is string => Boolean(childId && /^[0-9a-f-]{36}$/i.test(childId));

const emptyState = (): WorldState => ({ diamonds: 0, levels: {}, chests: {} });

export function readWorldState(childId: string | null): WorldState {
  try {
    const scoped = localStorage.getItem(storageKey(childId));
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    const parsed = JSON.parse(scoped || legacy || "{}");
    return { ...emptyState(), ...parsed, levels: parsed.levels || {}, chests: parsed.chests || {} };
  } catch {
    return emptyState();
  }
}

export function saveWorldState(childId: string | null, state: WorldState) {
  localStorage.setItem(storageKey(childId), JSON.stringify(state));
  // Keep the legacy key in sync while older challenge builds are still cached.
  localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(state));
}

export function recordWorldLevelResult(
  childId: string | null,
  id: string,
  stars: number,
  meta: { subject: string; level: number; title: string }
) {
  const state = readWorldState(childId);
  const previous = Number(state.levels[id]?.stars) || 0;
  state.levels = {
    ...state.levels,
    [id]: {
      ...state.levels[id],
      stars: Math.max(previous, stars),
      latestStars: stars,
      attempts: Number(state.levels[id]?.attempts || 0) + 1,
      completedAt: new Date().toISOString(),
      ...meta
    }
  };
  saveWorldState(childId, state);
  return previous;
}

export async function loadWorldState(childId: string | null): Promise<WorldState> {
  const local = readWorldState(childId);
  if (!supabase || !isCloudChild(childId)) return local;
  try {
    const { data, error } = await supabase
      .from("world_chest_claims")
      .select("subject_key,checkpoint,diamonds,claimed_at")
      .eq("child_id", childId);
    if (error || !data) return local;
    const chests = { ...local.chests };
    let diamonds = 0;
    data.forEach((row) => {
      const id = `${row.subject_key}-${row.checkpoint}`;
      const reward = Number(row.diamonds) || 0;
      diamonds += reward;
      chests[id] = { claimed: true, diamonds: reward, claimedAt: String(row.claimed_at || "") };
    });
    const next = { ...local, diamonds: Math.max(local.diamonds, diamonds), chests };
    saveWorldState(childId, next);
    return next;
  } catch {
    return local;
  }
}

export async function claimWorldChest(
  childId: string | null,
  subjectKey: string,
  checkpoint: number,
  reward: number
): Promise<{ state: WorldState; newlyClaimed: boolean }> {
  const id = `${subjectKey}-${checkpoint}`;
  const local = readWorldState(childId);
  if (local.chests[id]?.claimed) return { state: local, newlyClaimed: false };

  if (supabase && isCloudChild(childId)) {
    try {
      const { data, error } = await supabase.rpc("claim_world_chest", {
        p_child_id: childId,
        p_subject_key: subjectKey,
        p_checkpoint: checkpoint
      });
      if (!error && data) {
        const row = Array.isArray(data) ? data[0] : data;
        const next: WorldState = {
          ...local,
          diamonds: Number(row?.total_diamonds) || local.diamonds,
          chests: {
            ...local.chests,
            [id]: { claimed: true, diamonds: Number(row?.diamonds) || reward, claimedAt: String(row?.claimed_at || new Date().toISOString()) }
          }
        };
        saveWorldState(childId, next);
        return { state: next, newlyClaimed: Boolean(row?.newly_claimed) };
      }
    } catch {
      // The local idempotent record remains available if the migration is not live yet.
    }
  }

  const next: WorldState = {
    ...local,
    diamonds: local.diamonds + reward,
    chests: { ...local.chests, [id]: { claimed: true, diamonds: reward, claimedAt: new Date().toISOString() } }
  };
  saveWorldState(childId, next);
  return { state: next, newlyClaimed: true };
}
